import { useConfigs } from 'modules/configs';
import type { Entrypoint } from 'server/types';

type Link = { to: string; href?: never } | { to?: never; href: string };
type Product = 'intelligence' | 'assessment' | 'datasets' | 'alerts' | 'exploits' | 'mcp';
type Solution = 'securityAutomationSdk' | 'dataFeeds' | 'mcpForAiAgents' | 'exploitsAndKev' | 'whiteLabel';
type Resource = 'developerSdk' | 'apiReference' | 'ossAndTools' | 'changelog' | 'support';
type Company = 'pricing' | 'blog' | 'contacts' | 'about';

type SiteNavigation = {
    [entrypoint in Entrypoint]: {
        products: Record<Product, Link>;
        solutions: Record<Solution, Link>;
        resources: Record<Resource, Link>;
        company: Record<Company, Link>;
    };
};

const SITE_NAVIGATION = {
    main: {
        products: {
            intelligence: { to: '/products/vulnerability-intelligence' },
            assessment: { to: '/products/assessment' },
            datasets: { to: '/products/datasets' },
            alerts: { to: '/products/alerts' },
            exploits: { to: '/products/exploits' },
            mcp: { to: '/products/mcp' }
        },
        solutions: {
            securityAutomationSdk: { to: '/solutions/security-automation-sdk' },
            dataFeeds: { to: '/solutions/data-feeds' },
            mcpForAiAgents: { to: '/solutions/mcp-for-ai-agents' },
            exploitsAndKev: { to: '/solutions/exploits-and-kev' },
            whiteLabel: { to: '/solutions/white-label' }
        },
        resources: {
            developerSdk: { href: '/sdk' },
            apiReference: { href: 'https://docs.vulners.com/docs/api/' },
            ossAndTools: { href: 'https://github.com/vulnerscom' },
            changelog: { href: 'https://docs.vulners.com/changelog' },
            support: { to: '/company/contacts' }
        },
        company: {
            pricing: { to: '/pricing' },
            blog: { href: '/blog' },
            contacts: { to: '/company/contacts' },
            about: { to: '/company/about-us' }
        }
    },
    docs: {
        products: {
            intelligence: { href: '/docs/products/intelligence' },
            assessment: { href: '/docs/products/assessment' },
            datasets: { href: '/docs/products/datasets' },
            alerts: { href: '/docs/products/alerts' },
            exploits: { href: '/docs/products/exploits' },
            mcp: { href: '/docs/products/mcp' }
        },
        solutions: {
            securityAutomationSdk: { href: 'https://vulners.com/solutions/security-automation-sdk' },
            dataFeeds: { href: 'https://vulners.com/solutions/data-feeds' },
            mcpForAiAgents: { href: 'https://vulners.com/solutions/mcp-for-ai-agents' },
            exploitsAndKev: { href: 'https://vulners.com/solutions/exploits-and-kev' },
            whiteLabel: { href: 'https://vulners.com/solutions/white-label' }
        },
        resources: {
            developerSdk: { href: 'https://vulners.com/sdk' },
            apiReference: { href: '/docs/api/' },
            ossAndTools: { href: 'https://github.com/vulnerscom' },
            changelog: { to: '/changelog' },
            support: { href: 'https://vulners.com/company/contacts' }
        },
        company: {
            pricing: { href: 'https://vulners.com/pricing' },
            blog: { href: 'https://vulners.com/blog' },
            contacts: { href: 'https://vulners.com/company/contacts' },
            about: { href: 'https://vulners.com/company/about-us' }
        }
    }
} satisfies SiteNavigation;

export function useSiteNavigation() {
    const { entrypoint } = useConfigs();
    return SITE_NAVIGATION[entrypoint];
}
